import { StandardEvents } from '@shopify/events';

/**
 * Releasit owns #cart-wrapper and its button/form. Keep that portal mounted
 * through section morphs; only manage the surrounding cart lifecycle here.
 */
class ReleasitCartComponent extends HTMLElement {
  /** @type {Element | null} */
  #cart = null;
  /** @type {Element | null} */
  #drawer = null;
  /** @type {MutationObserver | undefined} */
  #observer;
  /** @type {Set<Promise<unknown>>} */
  #pending = new Set();
  #syncQueued = false;

  connectedCallback() {
    this.#cart = this.closest('cart-drawer-component, cart-items-component');
    this.#drawer = this.closest('theme-drawer');
    this.#observer = new MutationObserver(this.#queueSync);
    if (this.#cart) {
      this.#observer.observe(this.#cart, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    // Also covers Horizon's session-restored drawer, which need not emit open.
    if (this.#drawer) this.#observer.observe(this.#drawer, { attributes: true, attributeFilter: ['open'] });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartUpdate = (event) => {
    const promise = event.promise;
    if (!promise || this.#pending.has(promise)) return;
    this.#pending.add(promise);
    this.#sync();

    const settled = () => {
      this.#pending.delete(promise);
      this.#queueSync();
    };
    // A rejected/aborted cart update must unlock COD too. The cart owns errors.
    promise.then(settled, settled);
  };

  #queueSync = () => {
    if (this.#syncQueued) return;
    this.#syncQueued = true;
    queueMicrotask(() => {
      this.#syncQueued = false;
      if (this.isConnected) this.#sync();
    });
  };

  #sync() {
    if (!this.#cart) return;
    const empty = Boolean(this.#cart.querySelector('[data-cart-drawer-empty], .cart-page--empty'));
    const busy =
      this.#pending.size > 0 ||
      Boolean(this.#cart.querySelector('.cart-items-disabled')) ||
      this.#cart.classList.contains('cart-items-disabled');
    this.hidden = empty;
    this.inert = empty || busy;
    this.setAttribute('aria-busy', String(busy));

    // The current Releasit embed observes .active on cart-drawer-component;
    // Horizon 4 stores its actual open state on the ancestor theme-drawer.
    if (this.#drawer && this.#cart.matches('cart-drawer-component')) {
      const open = this.#drawer.hasAttribute('open');
      if (this.#cart.classList.contains('active') !== open) this.#cart.classList.toggle('active', open);
    }
  }
}

if (!customElements.get('releasit-cart-component')) {
  customElements.define('releasit-cart-component', ReleasitCartComponent);
}
